import {Injectable} from '@angular/core';
import {UsersService} from '../api/services/users.service';
import {UserProfile} from '../api/models/user-profile';
import {EditUserProfile} from '../api/models/edit-user-profile';
import {AccountService as ApiAccountService} from '../api/services/account.service';
import {UserDevicesService as ApiUserDevicesService} from '../api/services/user-devices.service';
import {AccountUser} from '../api/models/account-user';
import {LookupService} from '../api/services/lookup.service';
import {LookupModel} from '../api/models/lookup-model';
import {AccountRoleModel} from '../api/models/account-role-model';
import {AccountSettingsModel} from '../api/models/account-settings-model';
import {SaveAccountSettingsRequest} from '../api/models/save-account-settings-request';
import {AccountStorageModel} from '../api/models/account-storage-model';
import {RecordingsPlanModel} from '../api/models/recordings-plan-model';
import {RetentionWarningModel, TwoFactorSetupModel, UserDeviceModel} from '../api/models';

@Injectable()
export class AccountService {

  constructor(
    private apiUserService: UsersService,
    private apiAccountService: ApiAccountService,
    private apiUserDevicesService: ApiUserDevicesService,
    private lookupService: LookupService) {

  }

  async getUserProfile(): Promise<UserProfile> {
    const response = await this.apiUserService.usersProfile().toPromise();

    return response;
  }

  async getUserDevices(): Promise<UserDeviceModel[]> {
    const response = await this.apiUserDevicesService.userDevicesGetDevices().toPromise();

    return response;
  }

  async deleteUserDevice(id: string): Promise<boolean> {
    const response = await this.apiUserDevicesService.userDevicesDeleteDevice({id: id}).toPromise();

    return true;
  }

  async saveUserProfile(model: EditUserProfile): Promise<boolean> {
    const response = await this.apiUserService.usersSaveProfile({body: model}).toPromise();

    return true;
  }

  async getTwoFactorSetup(provider: string, phoneNumber?: string): Promise<TwoFactorSetupModel> {
    const response = await this.apiUserService.usersTwoFactorSetUp({provider: provider, phoneNumber: phoneNumber}).toPromise();

    return response;
  }

  async getTwoFactorVerify(provider: string, token: string, phoneNumber: string): Promise<boolean> {
    const response = await this.apiUserService.usersTwoFactorVerify({
      provider: provider,
      token: token,
      phoneNumber: phoneNumber
    }).toPromise();

    return response.success;
  }

  async saveAccountUser(model: AccountUser): Promise<boolean> {
    const response = await this.apiAccountService.accountUsersSaveUser({body: model}).toPromise();

    return true;
  }

  async deleteAccountUser(id: string): Promise<boolean> {
    const response = await this.apiAccountService.accountUsersDeleteUserById({id: id}).toPromise();

    return true;
  }

  public async getUserById(id: string): Promise<AccountUser> {
    if (id !== 'new') {
      const response = await this.apiAccountService.accountUsersGetUserById({id: id}).toPromise();

      return response;
    }
    return {
      id: 'new',
      username: '',
      isLocked: true,
      approved: true,
      emailConfirmed: false
    };
  }

  public async queryRolesLookup(state?: any): Promise<[LookupModel[], number]> {

    const response = await this.apiAccountService.accountUsersGetRoles().toPromise();

    return [response.data, response.total];
  }

  async getRoleById(id: string): Promise<AccountRoleModel> {
    if (id !== 'new') {
      const response = await this.apiAccountService.accountRolesGetRoleById({id: id}).toPromise();

      return response;
    }
    return {
      id: 'new',
      items: []
    };
  }

  async resendInvitation(email: string): Promise<boolean> {
    const response = await this.apiAccountService.accountUsersResendInvitation({email: email}).toPromise();

    return response;
  }

  async saveAccountRole(model: AccountRoleModel): Promise<boolean> {
    const response = await this.apiAccountService.accountRolesSaveRole({body: model}).toPromise();

    return true;
  }

  async deleteAccountRole(id: string): Promise<boolean> {
    const response = await this.apiAccountService.accountRolesDeleteRoleById({id: id}).toPromise();

    return true;
  }

  async getCompanySettings(): Promise<AccountSettingsModel> {
    const response = await this.apiAccountService.accountSettingsGetSettings().toPromise();

    return response;
  }

  async saveCompanySettings(model: SaveAccountSettingsRequest): Promise<AccountSettingsModel> {
    const response = await this.apiAccountService.accountSettingsSaveSettings({body: model}).toPromise();

    return response;
  }

  async getCompanyStorage(): Promise<AccountStorageModel> {
    const response = await this.apiAccountService.accountStorageGetCurrentUsage().toPromise();

    return response;
  }

  async saveRecordingsRetentionPlan(model: RecordingsPlanModel): Promise<AccountStorageModel> {
    const response = await this.apiAccountService.accountStorageSaveRecordingsRetention({body: model}).toPromise();

    return response;
  }

  async getRetentionWarning(model: RecordingsPlanModel): Promise<RetentionWarningModel> {
    const response = await this.apiAccountService.accountStorageGetRetentionWarning({body: model}).toPromise();

    return response;
  }
}
